"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Suspense, useEffect, useState, useSyncExternalStore } from "react";

import { CatalogFilters } from "~/app/_components/CatalogFilters";
import { useDeploymentConfig } from "~/app/_components/ConfigProvider";
import { useMobileNav } from "~/app/_components/MobileNavContext";
import { getCategories, type Category } from "~/network/categories";

/** Tailwind `lg` — only one CatalogFilters instance should mount at a time. */
const LG_UP_QUERY = "(min-width: 1024px)";

function subscribeLgUp(onChange: () => void) {
  const mq = window.matchMedia(LG_UP_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function useIsLgUp() {
  return useSyncExternalStore(
    subscribeLgUp,
    () => window.matchMedia(LG_UP_QUERY).matches,
    () => true,
  );
}

const FALLBACK_CATEGORIES: Category[] = [
  { id: "clothes", name: "Clothes", slug: "clothes" },
  { id: "shoes", name: "Shoes", slug: "shoes" },
  { id: "electronics", name: "Electronics", slug: "electronics" },
  { id: "accessories", name: "Accessories", slug: "accessories" },
  { id: "home_kitchen", name: "Home & Kitchen", slug: "home_kitchen" },
  { id: "beauty_care", name: "Beauty & Care", slug: "beauty_care" },
  { id: "sports_fitness", name: "Sports & Fitness", slug: "sports_fitness" },
  { id: "groceries", name: "Groceries", slug: "groceries" },
  { id: "toys_games", name: "Toys & Games", slug: "toys_games" },
  { id: "books", name: "Books", slug: "books" },
  {
    id: "phones_tablets",
    name: "Phones & Tablets",
    slug: "phones_tablets",
  },
  {
    id: "computers_laptops",
    name: "Computers & Laptops",
    slug: "computers_laptops",
  },
];

function CategoriesNav({
  categories,
  pathname,
  onNavigate,
}: {
  categories: Category[];
  pathname: string;
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      <span className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-stone-400">
        Categories
      </span>
      {categories.map((cat) => {
        const href = `/category/${cat.slug}`;
        const isActive = pathname === href || pathname.startsWith(`${href}/`);
        return (
          <Link
            key={cat.id}
            href={href}
            onClick={onNavigate}
            className={
              "rounded-lg px-3 py-2 text-sm transition " +
              (isActive
                ? "bg-brand-red text-white shadow-sm"
                : "text-stone-700 hover:bg-stone-200/80 hover:text-stone-900")
            }
          >
            {cat.name}
          </Link>
        );
      })}
    </nav>
  );
}

export function Sidebar() {
  const { config } = useDeploymentConfig();
  const [categories, setCategories] = useState<Category[]>(FALLBACK_CATEGORIES);
  const pathname = usePathname();
  const { isOpen, close } = useMobileNav();
  const isLgUp = useIsLgUp();
  const isCategoryPage = /^\/category\/[^/]+/.test(pathname);

  useEffect(() => {
    const deploymentBase = (
      config?.baseUrls?.deployment ??
      "https://objectstorage.apps.henga.co/diaspora-dollar/deployment/"
    ).replace(/\/?$/, "/");

    let cancelled = false;
    getCategories(deploymentBase)
      .then((list) => {
        if (!cancelled) setCategories(list);
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load categories.json:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [config]);

  return (
    <>
      <aside className="hidden w-64 shrink-0 border-r border-stone-200 bg-stone-50/50 py-6 lg:block">
        <CategoriesNav categories={categories} pathname={pathname} />
        {isCategoryPage && isLgUp ? (
          <Suspense fallback={null}>
            <CatalogFilters />
          </Suspense>
        ) : null}
      </aside>

      <div
        className={`fixed inset-0 z-40 lg:hidden ${isOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!isOpen}
      >
        <button
          type="button"
          className={`absolute inset-0 bg-stone-900/50 transition-opacity duration-200 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
          aria-label="Close categories menu"
          onClick={close}
          tabIndex={isOpen ? 0 : -1}
        />
        <aside
          id="mobile-categories-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Categories"
          className={`absolute left-0 top-0 flex h-full w-[min(18rem,85vw)] flex-col border-r border-stone-200 bg-stone-50 shadow-xl transition-transform duration-200 ease-out ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
            <span className="text-sm font-semibold text-stone-900">Categories</span>
            <button
              type="button"
              onClick={close}
              className="rounded-lg p-2 text-stone-600 hover:bg-stone-200/80 hover:text-stone-900"
              aria-label="Close categories menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-4">
            <CategoriesNav
              categories={categories}
              pathname={pathname}
              onNavigate={close}
            />
            {isCategoryPage && isOpen && !isLgUp ? (
              <Suspense fallback={null}>
                <CatalogFilters onNavigate={close} />
              </Suspense>
            ) : null}
          </div>
        </aside>
      </div>
    </>
  );
}
